/** Browser display grammar only. Canonical analytical validation stays on the server. */
import * as z from 'zod'
import { insightEntityTargetSchema } from '@testfeed/shopify-contracts'
const text = (max: number) => z.string().min(1).max(max)
const renderTarget = z.discriminatedUnion('kind', [
  insightEntityTargetSchema.options[0].strip(),
  ...insightEntityTargetSchema.options.slice(1).map((option) => option.strip()),
])
export const renderEntity = z.object({
  id: text(700),
  label: text(2000),
  imageUrl: z.string().nullable(),
  target: renderTarget.nullable().optional(),
})
export const renderSupportingRecords = z.object({
  title: text(700),
  description: text(2000),
  records: z.array(z.object({ entity: renderEntity, description: text(2000) })).min(1),
})
export type RenderSupportingRecords = z.infer<typeof renderSupportingRecords>
const ref = z.object({
  measurement: text(120).optional(),
  categoryKey: z.string().min(1).optional(),
  bandKey: text(120).optional(),
  field: text(120).optional(),
})
const unavailable = z.object({ state: z.literal('unavailable'), reason: text(120), message: text(200) })
const available = z.object({ state: z.literal('available'), value: z.number().finite().nonnegative() })
const common = { sourceRef: ref.optional(), label: text(120), baseRef: ref.optional() }
const numericRenderDatum = z.object({
  ...common,
  unit: z.enum(['count', 'proportion']),
  result: z.discriminatedUnion('state', [available, unavailable]),
})
const money = z.object({
  ...common,
  unit: z.literal('money'),
  currencyCode: z.string().regex(/^[A-Z]{3}$/),
  result: z.discriminatedUnion('state', [
    z.object({ state: z.literal('available'), value: z.string().regex(/^(?:0|[1-9][0-9]{0,13})\.[0-9]{4}$/) }),
    unavailable,
  ]),
})
export const renderDatum = z.union([numericRenderDatum, money])
export type RenderDatum = z.infer<typeof renderDatum>
const date = z.iso.datetime({ offset: true }).nullable()
const renderSource = z.discriminatedUnion('source', [
  z.object({ source: z.enum(['customers', 'orders', 'order_lines']), label: text(120), syncedAt: date }),
  z.object({ source: z.literal('observedWindow'), label: text(120), start: date, end: date }),
])
export const renderHelp = z.object({
  appliesTo: z.array(ref).max(100),
  why: text(500),
  how: text(700),
  notes: z
    .array(
      z.object({
        code: text(120),
        text: text(500),
        values: z.array(renderDatum).max(100),
        sources: z.array(renderSource).max(20),
      }),
    )
    .max(100),
})
export type RenderHelp = z.infer<typeof renderHelp>
export type RenderSource = z.infer<typeof renderSource>
const emphasis = z.enum(['primary', 'supporting']).optional()
const help = renderHelp.optional()
const metric = z.object({
  kind: z.literal('metric'),
  datum: numericRenderDatum,
  values: z.array(numericRenderDatum).optional(),
  emphasis,
  help,
})
const step = z.object({ datum: numericRenderDatum, emphasis })
const population = z.object({
  kind: z.literal('population-steps'),
  relationship: z.literal('nested-sets'),
  base: numericRenderDatum,
  steps: z.tuple([step, step, step]),
  help,
})
export const renderCardSchemas = { metric, 'population-steps': population }
export const renderCard = z.discriminatedUnion('kind', [metric, population])
export type SupportedRenderCard = z.infer<typeof renderCard>
export const renderPresentationShell = z.object({ sections: z.array(z.unknown()).min(1) })
export const renderSectionShell = z.object({
  id: text(80),
  title: text(120),
  help: renderHelp,
  cards: z.array(z.unknown()).min(1),
})
export const renderPageShell = z.object({ sections: z.array(z.unknown()).min(1).max(20) })
export const renderPageSectionShell = z.object({ id: z.string().regex(/^[a-z][a-z0-9-]{0,119}$/), kind: text(120) })

export const renderCategoryBreakdown = z.object({
  kind: z.literal('category-breakdown'),
  id: text(120),
  title: text(120),
  state: z.enum(['ready', 'empty', 'unavailable']),
  message: text(700).nullable(),
  base: numericRenderDatum,
  rows: z.array(
    z.object({
      key: z.string().min(1),
      label: z.string().min(1),
      count: numericRenderDatum,
      share: numericRenderDatum,
    }),
  ),
  previewRows: z.number().int().min(1).max(10),
  detailsLabel: text(120),
  footer: text(700),
  help: renderHelp,
})
export type RenderCategoryBreakdown = z.infer<typeof renderCategoryBreakdown>

export const renderRelationshipCard = z
  .object({
    kind: z.literal('relationship-list'),
    title: text(700),
    state: z.enum(['ready', 'empty', 'unavailable']),
    message: text(2000).nullable(),
    hero: z.object({ value: text(700), label: text(700), description: text(4000) }).nullable(),
    rows: z
      .array(
        z.object({
          id: text(2000),
          left: renderEntity,
          right: renderEntity,
          summary: text(2000),
          rateComparison: z.object({
            withoutPartnerPercent: z.number().min(0).max(100).nullable(),
            withPartnerPercent: z.number().min(0).max(100),
          }),
          note: text(2000),
          details: z
            .array(z.object({ label: text(6000), value: text(2000) }))
            .min(1)
            .max(20),
          supportingRecords: renderSupportingRecords.optional(),
        }),
      )
      .max(10),
    previewRows: z.number().int().min(1).max(3),
    detailsLabel: text(700).nullable(),
    footer: text(6000),
    help: renderHelp,
  })
  .superRefine((card, ctx) => {
    const ready = card.state === 'ready'
    if (
      ready !== card.rows.length > 0 ||
      ready !== (card.hero !== null) ||
      ready !== (card.detailsLabel !== null) ||
      ready === (card.message !== null) ||
      new Set(card.rows.map((r) => r.id)).size !== card.rows.length
    )
      ctx.addIssue({ code: 'custom', message: 'Relationship content and state disagree' })
  })
export type RenderRelationshipCard = z.infer<typeof renderRelationshipCard>
export const renderRelationships = renderRelationshipCard.safeExtend({
  id: z.string().regex(/^[a-z][a-z0-9-]{0,119}$/),
})
export type RenderRelationships = z.infer<typeof renderRelationships>
